import { CommonUtility } from '@utility/common';
import {
  BoldText,
  DangerText,
  MuteText,
  InfoTooltipPopup,
} from '@elements/Common';
import {
  ActionHoverTableRow,
  BorderTableCell,
  AppTableCell,
  AppTableRow,
} from '@components/AppTable';
import { Button, cn } from '@skupreme/shared/ui';
import { Fragment, useMemo, useState } from 'react';
import {
  ComponentProperties,
  ConversionViewer,
  ProductProperties,
} from '@page-components/scm/common';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import {
  CalendarIcon,
  CurrencyDollarIcon,
  PackageIcon,
  PencilSimpleIcon,
  TrashIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import { validateAttribute } from '@page-components/shipment';
import { CatalogHelpText } from '@utility/constants/strings';
import { ImageWithActionCell } from '@elements/Table';
import { CommonConstant } from '@utility/constants/common';
import { SemanticImageWithFallback } from '@components/FallbackImage';
import { DeleteConfirmWithButton } from '@components/DeleteConfirm';
import { ItemWiseAllocation } from './Allocation';
import {
  AddAliasButton,
  ALIAS_ACTION,
  AliasActions,
  AliasTableRow,
  CartonQtyCell,
  CartonQtyInput,
  ExpandToggle,
  ItemPrice,
  QtyErrorMessage,
  RowLine,
  SmallMuteText,
  TextAction,
} from './row-parts';

const PRODUCT_IMAGE =
  'sk:size-20 sk:aspect-square sk:object-contain sk:rounded-[5px] sk:border sk:border-solid sk:border-border-subtle sk:p-2!';

export function MultiAliasComponentRow({
  item,
  componentQty,
  qtyChanged,
  addEditAttribute,
  canTakeAction,
  isPublic,
  deleteAttribute,
  removeComponent,
  readOnly,
  editLeadTimeClicked,
  editPriceClicked,
  markUnitAvailableToShipClicked,
  componentWiseAllocations,
  imperial,
  currency,
}) {
  const [hover, setHover] = useState(false);
  const [expand, setExpand] = useState(false);
  const validAttributes = useMemo(
    () => (item.attributes || []).filter(x => !!x.alias),
    [item.attributes],
  );
  const latestAttribute = useMemo(
    () =>
      (validAttributes || []).length === 1 ? { ...validAttributes[0] } : {},
    [validAttributes],
  );

  const moqError = useMemo(
    () =>
      !!item.moq &&
      !!componentQty[item._id]?.qty &&
      item.moq > +componentQty[item._id].qty,
    [componentQty],
  );

  const isValidVariant = useMemo(
    () => validateAttribute(latestAttribute),
    [latestAttribute],
  );

  const menuActions = useMemo(() => {
    if (!canTakeAction) {
      return [];
    }
    const temp = [
      {
        title: 'Edit Item Cost',
        icon: <CurrencyDollarIcon />,
        onClick: () => editPriceClicked(item),
      },
    ];
    if (editLeadTimeClicked) {
      temp.push({
        title: 'Edit Lead Time',
        icon: <CalendarIcon />,
        onClick: () => editLeadTimeClicked(item),
      });
    }
    temp.push({
      title: 'Create an Alias',
      icon: <PackageIcon />,
      onClick: () => addEditAttribute(item),
    });
    if ((validAttributes || []).length === 1) {
      temp.push({
        title: 'Edit Current Alias',
        icon: <PencilSimpleIcon />,
        onClick: () => addEditAttribute(item, latestAttribute?.alias),
      });
    }
    if (
      (item.shippable_qty || 0) < componentQty[item._id]?.qty &&
      markUnitAvailableToShipClicked
    ) {
      temp.push({
        title: 'Mark Units Available to Ship',
        icon: <TruckIcon />,
        onClick: () => markUnitAvailableToShipClicked(item),
      });
    }
    if (removeComponent) {
      temp.push({
        title: 'Remove Component',
        icon: <TrashIcon />,
        onClick: () => removeComponent(item),
      });
    }
    return temp;
  }, [
    item,
    canTakeAction,
    editLeadTimeClicked,
    validAttributes,
    removeComponent,
    latestAttribute,
    componentQty,
    markUnitAvailableToShipClicked,
  ]);

  const minCartonQty = useMemo(() => {
    if (
      CommonUtility.isValidNumber(item?.moq) &&
      CommonUtility.isValidNumber(latestAttribute?.units_per_carton)
    ) {
      const cartons = Math.ceil(item.moq / latestAttribute.units_per_carton);
      return cartons - 1; // reduce 1 carton as message says greater than X cartons
    }

    return 0;
  }, [item]);

  return (
    <Fragment key={item._id}>
      <ActionHoverTableRow
        className={hover ? 'hover' : ''}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        textAlign="center"
        verticalAlign="middle"
      >
        <BorderTableCell expand={expand ? 'true' : 'false'}>
          <div className="border" />
          {/* @ts-expect-error - TS2741: Property 'className' is missing in type '{ image: Element; m... */}
          <ImageWithActionCell
            image={
              <SemanticImageWithFallback
                size="tiny"
                className={PRODUCT_IMAGE}
                src={item.images ? item.images[0] : CommonConstant.defaultImage}
              />
            }
            menus={menuActions}
          />
        </BorderTableCell>
        <AppTableCell textAlign="left">
          <RowLine>{item.title}</RowLine>
          <RowLine className="sk:justify-start">
            <MuteText className="sk:mr-2">SKU</MuteText>
            <BoldText>
              <TextCopyToClipboard text={item.sku} iconSize={14} />
            </BoldText>
          </RowLine>
          {componentWiseAllocations && componentWiseAllocations[item._id] && (
            <ItemWiseAllocation
              allocationData={componentWiseAllocations[item._id]}
              // @ts-expect-error - TS2322: Type '{ allocationData: any; shipments: never[]; supplierId: string; }' is not a...
              shipments={[]}
              supplierId="null"
            />
          )}
        </AppTableCell>
        <AppTableCell textAlign="left">
          {(validAttributes || []).length > 1 ? (
            <RowLine>
              <div>
                <MuteText>Multiple Aliases</MuteText>
                <ExpandToggle
                  expanded={expand}
                  onToggle={() => setExpand(!expand)}
                />
              </div>
              {!readOnly && (
                <AddAliasButton onPress={() => addEditAttribute(item)} />
              )}
            </RowLine>
          ) : (
            // @ts-expect-error - TS2741: Property 'isMultiple' is missing in type '{ imperial: any; unit: any; attributes...
            <ComponentProperties
              imperial={imperial}
              unit={item?.unit_of_measurement || 'units'}
              attributes={latestAttribute}
            />
          )}
        </AppTableCell>
        <CartonQtyCell width={2}>
          {(validAttributes || []).length ? (
            <>
              {validAttributes.length === 1 && (
                <>
                  {!isValidVariant ? (
                    latestAttribute.alias ? (
                      <TextAction
                        onClick={() =>
                          addEditAttribute(
                            {
                              ...item,
                              fulfillment_provider_id: item.origin_id,
                              attributes: [
                                {
                                  ...latestAttribute,
                                  attribute: latestAttribute,
                                },
                              ],
                            },
                            latestAttribute.alias,
                          )
                        }
                      >
                        <DangerText>Invalid Carton Configuration</DangerText>
                      </TextAction>
                    ) : (
                      <TextAction onClick={() => addEditAttribute(item)}>
                        <DangerText>Add New Carton Configuration</DangerText>
                      </TextAction>
                    )
                  ) : (
                    <>
                      {readOnly ? (
                        CommonUtility.numberWithCommas(
                          componentQty[item._id]?.aliases[latestAttribute.alias]
                            ?.ctn_qty,
                        )
                      ) : (
                        <CartonQtyInput
                          value={
                            componentQty[item._id]?.aliases
                              ? componentQty[item._id]?.aliases[
                                  latestAttribute.alias
                                ]?.ctn_qty
                              : null
                          }
                          onChange={value =>
                            qtyChanged(
                              item,
                              latestAttribute.alias,
                              'ctn_qty',
                              value,
                            )
                          }
                          isInvalid={moqError || !(item.prices || []).length}
                          maxValue={
                            200000000 / (latestAttribute?.units_per_carton || 1)
                          }
                        />
                      )}
                    </>
                  )}
                </>
              )}
              {moqError && (
                <QtyErrorMessage>
                  You must enter a Carton quantity greater than{' '}
                  {CommonUtility.numberWithCommas(minCartonQty)}
                </QtyErrorMessage>
              )}
            </>
          ) : (
            <TextAction onClick={() => addEditAttribute(item)}>
              <DangerText>Add New Carton Configuration</DangerText>
            </TextAction>
          )}
        </CartonQtyCell>
        <AppTableCell>
          <ConversionViewer
            originalUnit={latestAttribute.unit_of_measurement}
            value={componentQty[item._id]?.qty}
            imperial={imperial}
          />
        </AppTableCell>
        <AppTableCell>
          {(item.prices || []).length === 0 ? (
            <TextAction
              onClick={() => editPriceClicked(item)}
              title="Supplier Cost is not available, Click here to set it"
              className="sk:font-bold"
            >
              <DangerText>Set Cost</DangerText>
            </TextAction>
          ) : (
            <>
              {ItemPrice(
                readOnly,
                item.prices,
                componentQty[item._id]?.current_price,
                currency,
              )}
            </>
          )}
        </AppTableCell>
        <AppTableCell className="action-cell sk:whitespace-nowrap">
          {CommonUtility.currencyFormat(
            componentQty[item._id]?.total,
            currency,
            // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'NumberFormatOpti...
            null,
            4,
          )}
        </AppTableCell>
      </ActionHoverTableRow>
      {(validAttributes || []).length > 1 && expand && (
        <ChildItemList
          data={validAttributes}
          currentItem={item}
          currency={currency}
          componentQty={componentQty}
          qtyChanged={qtyChanged}
          editAttribute={addEditAttribute}
          deleteAttribute={deleteAttribute}
          readOnly={readOnly}
          isPublic={isPublic}
          hasError={moqError}
          imperial={imperial}
        />
      )}
    </Fragment>
  );
}

function AliasItemRow({
  item,
  componentQty,
  qtyChanged,
  attribute,
  editAttribute,
  index,
  readOnly,
  isPublic,
  deleteAttribute,
  hasError,
  imperial,
  currency,
}) {
  const currentAlias = useMemo(
    () => componentQty[item._id]?.aliases[attribute.alias],
    [componentQty, item, attribute],
  );
  const isValidVariant = useMemo(
    () => validateAttribute(attribute),
    [attribute],
  );

  return (
    <AliasTableRow
      textAlign="center"
      verticalAlign="middle"
      key={attribute.alias}
    >
      <BorderTableCell expand="true" colSpan="2" className="sk:pl-12!">
        <div className="border" />
        <RowLine>
          <SmallMuteText className="sk:flex sk:items-center">
            {attribute.primary ? 'Primary Carton Alias' : 'Carton Alias'}
            <InfoTooltipPopup
              position={isPublic && 'top right'}
              title={CatalogHelpText.carton}
            />
          </SmallMuteText>
        </RowLine>
        <RowLine>
          <BoldText>
            <TextCopyToClipboard text={attribute.alias} iconSize={14} />
          </BoldText>
        </RowLine>
        {!readOnly && (
          <AliasActions>
            <Button
              variant="primary"
              shape="circle"
              aria-label="Edit alias"
              isDisabled={!!readOnly}
              className={ALIAS_ACTION}
              onPress={() => editAttribute(item, attribute.alias)}
            >
              <PencilSimpleIcon size={16} aria-hidden />
            </Button>
            <DeleteConfirmWithButton
              disabled={!!readOnly}
              buttonClass={cn('sk:p-2!', ALIAS_ACTION)}
              size="mini"
              onConfirm={() => deleteAttribute(item, attribute.alias)}
              buttonText={<TrashIcon size={16} aria-label="Delete alias" />}
            />
          </AliasActions>
        )}
      </BorderTableCell>
      <AppTableCell>
        {/* @ts-expect-error - TS2741: Property 'isMultiple' is missing in type '{ imperial: any; u... */}
        <ComponentProperties
          imperial={imperial}
          unit={item.unit_of_measurement}
          attributes={attribute}
          showAlias={false}
        />
      </AppTableCell>
      <CartonQtyCell width={2}>
        {readOnly ? (
          CommonUtility.numberWithCommas(currentAlias?.ctn_qty)
        ) : (
          <>
            {isValidVariant ? (
              <CartonQtyInput
                value={currentAlias?.ctn_qty}
                onChange={value =>
                  qtyChanged(item, attribute.alias, 'ctn_qty', value)
                }
                isInvalid={hasError || !(item.prices || []).length}
                maxValue={200000000 / (attribute?.units_per_carton || 1)}
              />
            ) : (
              <TextAction onClick={() => editAttribute(item, index)}>
                <DangerText>Invalid Carton Configuration</DangerText>
              </TextAction>
            )}
          </>
        )}
      </CartonQtyCell>
      <AppTableCell>
        {CommonUtility.numberWithCommas(currentAlias?.qty || '')}
      </AppTableCell>
      <AppTableCell />
      <AppTableCell className="action-cell sk:whitespace-nowrap">
        {CommonUtility.currencyFormat(
          (currentAlias?.qty || 0) *
            (componentQty[item._id]?.current_price || 0),
          currency,
          // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'NumberFormatOpti...
          null,
          4,
        )}
      </AppTableCell>
    </AliasTableRow>
  );
}

function ChildItemList({
  data,
  componentQty,
  qtyChanged,
  currentItem,
  editAttribute,
  deleteAttribute,
  readOnly,
  isPublic,
  hasError,
  imperial,
  currency,
}) {
  return (
    <>
      {(data || []).map((item, index) => (
        <AliasItemRow
          key={item.alias}
          item={currentItem}
          attribute={item}
          componentQty={componentQty}
          qtyChanged={qtyChanged}
          editAttribute={editAttribute}
          index={index}
          readOnly={readOnly}
          isPublic={isPublic}
          deleteAttribute={deleteAttribute}
          hasError={hasError}
          imperial={imperial}
          currency={currency}
        />
      ))}
    </>
  );
}

function ConfirmAliasItemRow({ attribute, item, imperial, currency }) {
  return (
    <AliasTableRow
      textAlign="center"
      verticalAlign="middle"
      key={attribute.alias}
    >
      <BorderTableCell expand="true" colSpan="2" className="sk:pl-12!">
        <div className="border" />
        <RowLine>
          <SmallMuteText className="sk:flex sk:items-center">
            {attribute.primary ? 'Primary Carton Alias' : 'Carton Alias'}
            <InfoTooltipPopup
              position="top right"
              title={CatalogHelpText.carton}
            />
          </SmallMuteText>
        </RowLine>
        <RowLine>
          <BoldText>
            <TextCopyToClipboard text={attribute.alias} iconSize={14} />
          </BoldText>
        </RowLine>
      </BorderTableCell>
      <AppTableCell>
        {item.unit_of_measurement ? (
          // @ts-expect-error - TS2741: Property 'isMultiple' is missing in type '{ imperial: any; unit: any; attributes...
          <ComponentProperties
            imperial={imperial}
            unit={item?.unit_of_measurement || 'units'}
            attributes={attribute}
          />
        ) : (
          /* @ts-expect-error - TS2741: Property 'isMultiple' is missing in type '{ attributes: any;... */
          <ProductProperties attributes={attribute} />
        )}
      </AppTableCell>
      <CartonQtyCell width={2}>
        {CommonUtility.numberWithCommas(attribute?.ctn_qty)}
      </CartonQtyCell>
      <AppTableCell>
        <div className="sk:whitespace-nowrap">
          <ConversionViewer
            value={attribute.qty}
            originalUnit={item?.unit_of_measurement}
            imperial={imperial}
          />
        </div>
      </AppTableCell>
      <AppTableCell />
      <AppTableCell className="action-cell sk:whitespace-nowrap">
        {CommonUtility.currencyFormat(
          (attribute?.qty || 0) * (item.current_price || 0),
          currency,
          // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'NumberFormatOpti...
          null,
          4,
        )}
      </AppTableCell>
    </AliasTableRow>
  );
}

function ConfirmChildItemList({ data, currentItem, imperial, currency }) {
  return (
    <>
      {(data || []).map(item => (
        <ConfirmAliasItemRow
          key={item.alias}
          attribute={item}
          item={currentItem}
          imperial={imperial}
          currency={currency}
        />
      ))}
    </>
  );
}

export function ConfirmItemRow({ item, imperial, currency }) {
  const [expand, setExpand] = useState(false);
  const attributes = useMemo(() => Object.values(item.aliases), [item]);
  const latestAttribute = useMemo(
    // @ts-expect-error - TS2698: Spread types may only be created from object types.
    () => ((attributes || []).length === 1 ? { ...attributes[0] } : {}),
    [attributes],
  );

  return (
    <Fragment key={item._id}>
      <AppTableRow textAlign="center" verticalAlign="middle">
        <BorderTableCell expand={expand ? 'true' : 'false'}>
          <div className="border" />
          {!item.isComponent && (
            <SemanticImageWithFallback
              className={PRODUCT_IMAGE}
              size="tiny"
              src={item.image}
            />
          )}
        </BorderTableCell>
        <AppTableCell textAlign="left">
          <RowLine>{item.title}</RowLine>
          <RowLine>
            <BoldText>
              <TextCopyToClipboard text={item.sku} iconSize={14} />
            </BoldText>
          </RowLine>
        </AppTableCell>
        <AppTableCell textAlign="left">
          {!item.isComponent && (
            <>
              {(attributes || []).length > 1 ? (
                <RowLine>
                  <div>
                    <MuteText>Multiple Aliases</MuteText>
                    <ExpandToggle
                      expanded={expand}
                      onToggle={() => setExpand(!expand)}
                    />
                  </div>
                </RowLine>
              ) : item.unit_of_measurement ? (
                // @ts-expect-error - TS2741: Property 'isMultiple' is missing in type '{ imperial: any; unit: any; attributes...
                <ComponentProperties
                  imperial={imperial}
                  unit={item?.unit_of_measurement || 'units'}
                  attributes={latestAttribute}
                />
              ) : (
                /* @ts-expect-error - TS2741: Property 'isMultiple' is missing in type '{ attributes: any;... */
                <ProductProperties attributes={latestAttribute} />
              )}
            </>
          )}
        </AppTableCell>
        <AppTableCell width={2}>
          {CommonUtility.numberWithCommas(item.ctn_qty)}
        </AppTableCell>
        <AppTableCell>
          <div className="sk:whitespace-nowrap">
            <ConversionViewer
              value={item.qty}
              originalUnit={item?.unit_of_measurement}
              imperial={imperial}
            />
          </div>
        </AppTableCell>
        <AppTableCell>
          {item.current_price ? (
            // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'NumberFormatOpti...
            CommonUtility.currencyFormat(item.current_price, currency, null, 4)
          ) : (
            <DangerText>Set Cost</DangerText>
          )}
        </AppTableCell>
        <AppTableCell className="action-cell sk:whitespace-nowrap">
          {/* @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of ty... */}
          {CommonUtility.currencyFormat(item.total, currency, null, 4)}
        </AppTableCell>
      </AppTableRow>
      {expand && (
        <ConfirmChildItemList
          imperial={imperial}
          data={attributes}
          currency={currency}
          currentItem={item}
        />
      )}
    </Fragment>
  );
}
